/**
 * Templates: the starting setup an organisation chooses at sign-up, and the
 * way an existing organisation adds a stream from the same definitions in
 * Settings. The definitions live on the server (`routes/v1/templates.rs`);
 * this only reads and applies them.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

export type TemplateSummary = components['schemas']['TemplateSummary'];
export type TemplatePreview = components['schemas']['TemplatePreview'];
export type ApplyTemplateResponse = components['schemas']['ApplyTemplateResponse'];

const KEY = ['templates'] as const;

/** Every template, and what this organisation already has. Admins only. */
export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/templates', {}), 'TemplateListResponse'),
    staleTime: 60 * 1000,
  });
}

/** What applying one would do here, before anything is written. */
export function useTemplatePreview(key: string | null, onboarding: boolean) {
  return useQuery({
    queryKey: [...KEY, 'preview', key, onboarding],
    enabled: key != null,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/templates/{key}/preview', {
          params: { path: { key: key ?? '' }, query: { onboarding } },
        }),
        'TemplatePreview',
      ),
    staleTime: 0,
  });
}

/**
 * Apply one. At sign-up (`onboarding`) it also records that the question is
 * answered; from Settings it only adds. Everything a template can make is
 * refreshed after.
 */
export function useApplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, onboarding }: { key: string; onboarding: boolean }) =>
      unwrap(
        await api.POST('/api/v1/templates/{key}/apply', {
          params: { path: { key } },
          body: { onboarding },
        }),
        'ApplyTemplateResponse',
      ),
    onSuccess: (_data, vars) => {
      // Sign-up answered the question, so it is not asked again.
      if (vars.onboarding) {
        qc.setQueryData(['settings', 'vocabulary'], (old: object | undefined) => ({ ...old, chosen: true }));
      }
      for (const key of [KEY, ['campaigns'], ['campaign'], ['routes'], ['assignment'], ['pipelines'], ['settings', 'returns']]) {
        qc.invalidateQueries({ queryKey: key });
      }
    },
  });
}
